// Dependency-free i18n: a flat t(key, vars) reader over a nested string
// catalog, English bundled synchronously (it's the fallback every other
// locale falls back to on a missing key, so it must always be in memory),
// every other locale loaded on demand via dynamic import() so 22 languages
// of copy don't bloat the initial bundle (CLAUDE.md: minimise footprint).
// See DECISIONS.md for why this is hand-rolled rather than an i18n
// library (no new dependency).
import en from './strings/en.js';
import { LANGUAGES, isRTL } from './languages.js';

export { isRTL };
export const DEFAULT_LANGUAGE = 'en';

const LOCALE_LOADERS = {
  en: () => Promise.resolve({ default: en }),
  fr: () => import('./strings/fr.js'),
  de: () => import('./strings/de.js'),
  hi: () => import('./strings/hi.js'),
  id: () => import('./strings/id.js'),
  it: () => import('./strings/it.js'),
  ja: () => import('./strings/ja.js'),
  ko: () => import('./strings/ko.js'),
  pt: () => import('./strings/pt.js'),
  'es-419': () => import('./strings/es-419.js'),
  es: () => import('./strings/es.js'),
  'zh-Hans': () => import('./strings/zh-Hans.js'),
  'zh-Hant': () => import('./strings/zh-Hant.js'),
  ar: () => import('./strings/ar.js'),
  vi: () => import('./strings/vi.js'),
  ur: () => import('./strings/ur.js'),
  tl: () => import('./strings/tl.js'),
  tr: () => import('./strings/tr.js'),
  uk: () => import('./strings/uk.js'),
  ru: () => import('./strings/ru.js'),
  th: () => import('./strings/th.js'),
  nl: () => import('./strings/nl.js'),
  pl: () => import('./strings/pl.js')
};

export function supportedLanguages() {
  return LANGUAGES;
}

let currentCode = DEFAULT_LANGUAGE;
let currentCatalog = en;
// One in-flight/cached promise per code — a user flipping the dropdown
// back and forth shouldn't re-fetch a chunk that's already loaded, and
// concurrent calls should share the same import() rather than racing.
const cache = { en };

// Loads (or reuses) the catalog for `code` and makes it current. Never
// rejects visibly to the caller: an unknown code or a failed chunk fetch
// (offline right after install, before the SW has cached it) just keeps
// whatever catalog was already active — t() always has *something* to
// read, worst case English.
export async function setLanguage(code) {
  if (!LOCALE_LOADERS[code]) code = DEFAULT_LANGUAGE;
  if (cache[code]) {
    currentCode = code;
    currentCatalog = cache[code];
    return currentCode;
  }
  try {
    const mod = await LOCALE_LOADERS[code]();
    cache[code] = mod.default;
    currentCode = code;
    currentCatalog = mod.default;
  } catch {
    // Chunk unreachable — stay on whatever was active; t()'s own
    // catalog->English fallback covers any gap.
  }
  return currentCode;
}

export function getLanguage() {
  return currentCode;
}

function lookup(catalog, path) {
  return path.split('.').reduce((node, key) => (node && typeof node === 'object' ? node[key] : undefined), catalog);
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (m, key) => (key in vars ? String(vars[key]) : m));
}

// t('screen.key', { placeholder: value }) — looks up `path` in the active
// catalog, falls back to the English catalog on a missing key (so a
// locale file that's missing a newer key degrades to readable English
// instead of a blank string or a raw dotted key), and finally to the path
// itself as a last resort so a genuinely missing key is at least visible
// and greppable in the UI rather than silently empty.
export function t(path, vars) {
  const value = lookup(currentCatalog, path);
  if (typeof value === 'string') return interpolate(value, vars);
  const fallback = lookup(en, path);
  if (typeof fallback === 'string') return interpolate(fallback, vars);
  return path;
}
